/**
 * TextSettings - настройки текстового элемента (заголовок, подзаголовок и т.д.)
 * Переиспользуется везде где нужен текст с настройками
 */
import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

// Опции размеров
const sizeOptions = [
  { value: 'sm', label: 'S' },
  { value: 'base', label: 'M' },
  { value: 'lg', label: 'L' },
  { value: 'xl', label: 'XL' },
  { value: '2xl', label: '2XL' },
  { value: '3xl', label: '3XL' },
  { value: '4xl', label: '4XL' },
];

// Опции веса с классами для визуализации
const weightOptions = [
  { value: 'normal', label: 'Аа', className: 'font-normal' },
  { value: 'medium', label: 'Аа', className: 'font-medium' },
  { value: 'semibold', label: 'Аа', className: 'font-semibold' },
  { value: 'bold', label: 'Аа', className: 'font-bold' },
];

// Опции цвета с визуализацией
const colorOptions = [
  { value: 'inherit', label: 'Авто', bg: 'bg-gradient-to-r from-foreground to-text-secondary' },
  { value: 'white', label: 'Белый', bg: 'bg-white border border-border' },
  { value: 'white-muted', label: 'Приглуш.', bg: 'bg-white/70 border border-border' },
  { value: 'foreground', label: 'Основной', bg: 'bg-foreground' },
  { value: 'text-secondary', label: 'Вторичный', bg: 'bg-text-secondary' },
  { value: 'primary', label: 'Акцент', bg: 'bg-primary' },
];

export default function TextSettings({
  label,
  value = {},           // { text, size, weight, color, multiline }
  onChange,
  placeholder = 'Введите текст',
  multiline: defaultMultiline = false,
  rows = 3,
  defaults = {},        // Значения по умолчанию для этого контекста
  showSize = true,
  showWeight = true,
  showColor = true,
  showMultiline = true,
}) {
  const [showStyles, setShowStyles] = useState(false);

  // Получаем значения с учётом defaults
  const text = value?.text ?? '';
  const size = value?.size ?? defaults.size ?? 'base';
  const weight = value?.weight ?? defaults.weight ?? 'normal';
  const italic = value?.italic ?? defaults.italic ?? false;
  const color = value?.color ?? defaults.color ?? 'inherit';
  const isMultiline = value?.multiline ?? defaultMultiline;

  const updateField = (field, newValue) => {
    const updated = {
      ...value,
      text: value?.text ?? '',
      [field]: newValue,
    };
    // Если multiline включен по умолчанию из схемы, сохраняем в данные
    if (defaultMultiline && updated.multiline === undefined) {
      updated.multiline = true;
    }
    onChange(updated);
  };

  const hasStyleOptions = showSize || showWeight || showColor || showMultiline;
  const hasCustomStyles = value?.size || value?.weight || value?.italic || value?.color || value?.multiline;

  return (
    <div className="space-y-2">
      {label && (
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium">{label}</label>
          {hasStyleOptions && (
            <button
              onClick={() => setShowStyles(!showStyles)}
              className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${
                showStyles || hasCustomStyles
                  ? 'bg-primary/10 text-primary'
                  : 'text-text-secondary hover:bg-muted'
              }`}
              title="Настройки стиля"
            >
              <Icon name="Paintbrush" size={12} />
              <span>Стиль</span>
              <Icon name={showStyles ? 'ChevronUp' : 'ChevronDown'} size={12} />
            </button>
          )}
        </div>
      )}

      {/* Панель стилей - НАД полем ввода */}
      {hasStyleOptions && showStyles && (
        <div className="flex flex-wrap items-center gap-2 p-2 bg-muted rounded-lg">
          {/* Размер */}
          {showSize && (
            <select
              value={size}
              onChange={(e) => updateField('size', e.target.value)}
              className="pl-2 pr-6 py-1 text-xs bg-background border border-border rounded cursor-pointer"
              title="Размер"
            >
              {sizeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          )}

          {/* Вес - кнопки с визуализацией */}
          {showWeight && (
            <div className="flex border border-border rounded overflow-hidden">
              {weightOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => updateField('weight', opt.value)}
                  className={`px-2 py-1 text-sm ${opt.className} transition-colors ${
                    weight === opt.value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background hover:bg-muted'
                  }`}
                  title={opt.value}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}

          {/* Курсив - toggle кнопка */}
          {showWeight && (
            <button
              onClick={() => updateField('italic', !italic)}
              className={`px-2 py-1 border border-border rounded transition-colors ${
                italic
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background hover:bg-muted'
              }`}
              title="Курсив"
            >
              <Icon name="Italic" size={14} />
            </button>
          )}

          {/* Цвет - выпадающий с индикатором */}
          {showColor && (
            <div className="relative">
              <select
                value={color}
                onChange={(e) => updateField('color', e.target.value)}
                className="pl-6 pr-6 py-1 text-xs bg-background border border-border rounded cursor-pointer appearance-none"
                title="Цвет"
              >
                {colorOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {/* Индикатор цвета слева */}
              <span
                className={`absolute left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 rounded-sm ${colorOptions.find(o => o.value === color)?.bg || ''}`}
              />
              {/* Стрелка справа */}
              <Icon
                name="ChevronDown"
                size={12}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-text-secondary"
              />
            </div>
          )}

          {/* Многострочный */}
          {showMultiline && (
            <>
              <div className="w-px h-5 bg-border" />
              <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                <input
                  type="checkbox"
                  checked={isMultiline}
                  onChange={(e) => updateField('multiline', e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-border text-primary focus:ring-primary"
                />
                <span>Многострочный</span>
              </label>
            </>
          )}
        </div>
      )}

      {/* Текст */}
      {isMultiline ? (
        <textarea
          value={text}
          onChange={(e) => updateField('text', e.target.value)}
          placeholder={placeholder}
          rows={rows}
          className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none text-base md:text-sm"
        />
      ) : (
        <Input
          value={text}
          onChange={(e) => updateField('text', e.target.value)}
          placeholder={placeholder}
        />
      )}
    </div>
  );
}

// Экспорт опций для использования в других местах
TextSettings.sizeOptions = sizeOptions;
TextSettings.weightOptions = weightOptions;
TextSettings.colorOptions = colorOptions;
